import { cn } from '@newfront-insurance/core-ui/v2';
import { forwardRef, type ButtonHTMLAttributes, type HTMLAttributes, type KeyboardEventHandler } from 'react';

export const FEEDBACK_ACTION_GRADIENT = 'bg-[linear-gradient(150deg,#97ECE3_2%,#DBC1F4_82%)]';

export const FEEDBACK_POPOVER_POSITION = {
  align: 'start',
  collisionPadding: 16,
  side: 'bottom',
  sideOffset: 8,
} as const;

export const FEEDBACK_POPOVER_SHELL_CLASS_NAME =
  'border-steel-200 z-[1500] max-h-[calc(100vh-2rem)] overflow-auto rounded-[0.25rem] border bg-white p-0 shadow-xl';

export function FeedbackReasonGroup({ children, onKeyDown, ...props }: HTMLAttributes<HTMLDivElement>) {
  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    onKeyDown?.(event);
    if (
      event.defaultPrevented ||
      !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)
    ) {
      return;
    }

    const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]'));
    const currentIndex = buttons.indexOf(event.currentTarget.ownerDocument.activeElement as HTMLButtonElement);
    if (currentIndex < 0) return;

    event.preventDefault();
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? buttons.length - 1
          : (currentIndex + (['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1) + buttons.length) %
            buttons.length;
    buttons[nextIndex]?.focus();
  };

  return (
    <div {...props} role="radiogroup" onKeyDown={handleKeyDown}>
      {children}
    </div>
  );
}

interface FeedbackReasonButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  selected: boolean;
}

export const FeedbackReasonButton = forwardRef<HTMLButtonElement, FeedbackReasonButtonProps>(
  ({ children, className, selected, ...props }, ref) => (
    <button
      {...props}
      ref={ref}
      type="button"
      role="radio"
      aria-checked={selected}
      className={cn(
        'h-8 rounded-full border px-3 text-sm leading-5 transition-opacity',
        'focus-visible:outline-wtw-infinity-500 focus-visible:outline-2 focus-visible:outline-offset-2',
        selected
          ? cn('border-transparent text-[#350E5E]', FEEDBACK_ACTION_GRADIENT)
          : 'border-steel-200 text-steel-400 hover:border-steel-300 bg-white',
        className,
      )}
    >
      {children}
    </button>
  ),
);

FeedbackReasonButton.displayName = 'FeedbackReasonButton';
